import { Command as CommandPrimitive } from "cmdk";
import * as React from "react";
import { Button } from "@volca/ui/components/ui/button";
import {
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@volca/ui/components/ui/command";
import { Skeleton } from "@volca/ui/components/ui/skeleton";
import { cn } from "@volca/ui/lib/utils";
import { AUTOCOMPLETE_POPUP_CLASS } from "./autocomplete-field";

type Option = { value: string; label: string };

interface AutoCompleteListProps {
  listRef: React.Ref<HTMLDivElement>;
  open: boolean;
  items: Option[];
  selectedValue: string;
  onSelectOption: (value: string) => void;
  renderOption?: (option: Option) => React.ReactNode;
  isLoading?: boolean;
  emptyMessage: string;
  scrollAreaClassName?: string;
  containScroll?: boolean;
  onAddNew?: () => void;
  addNewLabel: string;
  onAddNewHover: () => void;
  onAddNewPress: () => void;
}

export const AutoCompleteList = ({
  listRef,
  open,
  items,
  selectedValue,
  onSelectOption,
  renderOption,
  isLoading,
  emptyMessage,
  scrollAreaClassName,
  containScroll,
  onAddNew,
  addNewLabel,
  onAddNewHover,
  onAddNewPress,
}: AutoCompleteListProps) => (
  <div className="relative">
    <div className={cn(AUTOCOMPLETE_POPUP_CLASS, { hidden: !open })}>
      <CommandList
        ref={listRef}
        className={cn(
          "max-h-44",
          { "overscroll-contain": containScroll },
          scrollAreaClassName,
        )}
        onWheel={containScroll ? (event) => event.stopPropagation() : undefined}
      >
        {items.length > 0 && !isLoading ? (
          <CommandGroup className="p-0">
            {items.map((option) => (
              <CommandItem
                key={option.value}
                value={option.value}
                keywords={[option.label]}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onSelect={() => onSelectOption(option.value)}
                data-checked={selectedValue === option.value}
                className="data-checked:bg-selected data-checked:text-brand"
              >
                {renderOption ? renderOption(option) : option.label}
              </CommandItem>
            ))}
          </CommandGroup>
        ) : null}
      </CommandList>
      {/* Loading and empty sit beside the listbox, not in it: a listbox may
          only hold options, so it stays empty until there are some. */}
      {isLoading ? (
        <CommandPrimitive.Loading>
          <Skeleton className="h-8 w-full" />
        </CommandPrimitive.Loading>
      ) : null}
      {!isLoading && items.length === 0 ? (
        <CommandEmpty className="py-3">{emptyMessage}</CommandEmpty>
      ) : null}
      {onAddNew && (
        <Button
          variant="ghost"
          color="brand"
          className="w-full justify-start"
          onMouseEnter={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onAddNewHover();
          }}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onAddNewPress();
          }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onAddNew();
          }}
        >
          {addNewLabel}
        </Button>
      )}
    </div>
  </div>
);
